import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getOrder } from '../../../store/order/api';
import { Order } from '../../../store/order/api/interface';
import { useAppDispatch, useAppSelector } from '../../../store';
import { IParams, OrderStatus } from '../../../enum';
import { decryptString } from '../../../utils/url';
import Loader from '../../../components/Loader';
import Page from '../../../components/Page';
import Breadcrumbs from '../../../components/Breadcrumbs';
import ViewOrder from './ViewOrder';
import Iconify from '../../../components/Iconify';
import useToggle from '../../../hooks/useToggle';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { createOrderStatusChange } from '../../../store/orderStatusChange/api';
import { IsNotNullOrEmpty } from '../../../utils/valid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FaDownload } from 'react-icons/fa6';
import { getDownloadInvoice } from '../../../store/downloadInvoice/api';
import { mergeImages } from '../../../utils/mergeImages';
import { getAssetURl } from '../../../utils/url';
import { generatePDFFromHTMLString } from '../../../utils/htmlToPdf';
import { generateOrderDetailsHTML, prepareOrderBundleImagesForPDF } from '../../../utils/orderPdfTemplate';
import { findColor } from '../../../store/color/api';
import ExactDeliverySlotSelector from '../../../components/ExactDeliverySlotSelector';
import CheckLateOrdersButton from '../../../components/CheckLateOrdersButton';
import { RegenerateOrderPaymentLink } from '../../../client/services';
import { toast } from 'react-toastify';

const ViewManagePO = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const location = useLocation();
  const tempOrderView = location.pathname.includes('/temp-order');
  const { isLoading } = useAppSelector((state) => state.order);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const assetUrl = useAppSelector((state) => state.active.selectedItem.assetUrl);
  const { data: paletteColors } = useAppSelector((state) => state.color);
  const params = useParams<IParams>();
  const [order, setOrder] = useState<Order>();
  const [refundForCancel, setRefundForCancel] = useState<boolean>(true);
  const [mergedImages, setMergedImages] = useState<string[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const { toggle, onOpen, onClose } = useToggle();
  const [regeneratingLink, setRegeneratingLink] = useState(false);
  const [paymentLinkDialog, setPaymentLinkDialog] = useState<{
    open: boolean;
    url: string;
  }>({ open: false, url: '' });

  useEffect(() => {
    dispatch(findColor({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    const fetchById = async () => {
      const decodeParam = decryptString(params?.id ?? '');
      try {
        const record = await dispatch(getOrder({ id: decodeParam })).unwrap();
        setOrder(record);
      } catch (error: any) {}
    };
    fetchById();
  }, [dispatch, navigation, params?.id]);

  // Generate merged images when order data is available
  useEffect(() => {
    // Reset images loaded state when order changes
    setImagesLoaded(false);
    
    const generateMergedImages = async () => {
      if (order?.order_items) {
        try {
          console.log("Generating merged images for order items...");
          const allMergedImages = await Promise.all(
            order.order_items.map(async (item: any, index: number) => {
              console.log(`Processing item ${index}:`, item);
              if (item?.order_cake) {
                try {
                  const displayMode = item.order_cake.displayMode || 'front';
                  console.log(`Item ${index} display mode:`, displayMode);
                  
                  if (displayMode === 'front') {
                    console.log(`Item ${index} front image check:`, {
                      hasShape: !!item.order_cake.shape?.frontImg,
                      hasFlavor: !!item.order_cake.flavor?.shapeImages?.[0]?.front,
                      shapeImg: item.order_cake.shape?.frontImg,
                      flavorImg: item.order_cake.flavor?.shapeImages?.[0]?.front
                    });
                    if (
                      item.order_cake.shape?.frontImg &&
                      item.order_cake.flavor?.shapeImages?.[0]?.front
                    ) {
                      console.log("Generating front merged image for cake", index);
                      return await mergeImages(
                        getAssetURl(item.order_cake.shape.frontImg, assetUrl),
                        getAssetURl(item.order_cake.flavor.shapeImages[0].front, assetUrl),
                        item.order_cake.colorCode,
                        getAssetURl(
                          item.order_cake.topping_category_item?.toppingShapeImages?.[0]?.front,
                          assetUrl
                        ),
                        getAssetURl(item.order_cake.shape?.frontColorImg, assetUrl)
                      );
                    } else {
                      console.log(`Item ${index}: Missing required images for front display mode`);
                    }
                  } else if (displayMode === 'top') {
                    console.log(`Item ${index} top image check:`, {
                      hasShape: !!item.order_cake.shape?.topImg,
                      hasFlavor: !!item.order_cake.flavor?.shapeImages?.[0]?.top,
                      shapeImg: item.order_cake.shape?.topImg,
                      flavorImg: item.order_cake.flavor?.shapeImages?.[0]?.top
                    });
                    if (
                      item.order_cake.shape?.topImg &&
                      item.order_cake.flavor?.shapeImages?.[0]?.top
                    ) {
                      console.log("Generating top merged image for cake", index);
                      return await mergeImages(
                        getAssetURl(item.order_cake.shape.topImg, assetUrl),
                        getAssetURl(item.order_cake.flavor.shapeImages[0].top, assetUrl),
                        item.order_cake.colorCode,
                        getAssetURl(
                          item.order_cake.topping_category_item?.toppingShapeImages?.[0]?.top,
                          assetUrl
                        ),
                        getAssetURl(item.order_cake.shape?.topColorImg, assetUrl)
                      );
                    } else {
                      console.log(`Item ${index}: Missing required images for top display mode`);
                    }
                  }
                } catch (error) {
                  console.error("Error generating merged image:", error);
                }
              } else {
                console.log(`Item ${index}: Not a cake item`);
              }
              return null;
            })
          );
          
          const validImages = allMergedImages.filter((img): img is string => img !== null);
          console.log("Generated merged images:", validImages.length, "out of", order.order_items.length);
          console.log("All merged images array:", allMergedImages);
          console.log("Valid images array:", validImages);
          setMergedImages(validImages);
          
          // Mark images as loaded after a short delay to ensure all images are fully processed
          setTimeout(() => {
            setImagesLoaded(true);
            console.log("Images marked as loaded");
          }, 1000);
        } catch (error) {
          console.error("Error generating merged images:", error);
          // Even if there's an error, mark as loaded to prevent infinite loading state
          setImagesLoaded(true);
        }
      }
    };

    generateMergedImages();
  }, [order, assetUrl]);

  const handleClose = () => {
    setRefundForCancel(true);
    onClose();
  };

  const handleNo = async () => {
    setRefundForCancel(true);
    onClose();
  };

  const handleYes = async () => {
    if (order?.id) {
      const result = await dispatch(
        createOrderStatusChange({
          orderId: order?.id,
          status: OrderStatus.cancelled,
          isRefund: refundForCancel,
        })
      ).unwrap();
      if (IsNotNullOrEmpty(result)) {
        toast.success('Order cancelled');
        const decodeParam = decryptString(params?.id ?? '');
        const record = await dispatch(getOrder({ id: decodeParam })).unwrap();
        setOrder(record);
      }
    }
    setRefundForCancel(true);
    onClose();
  };

  const handleBack = async () => {
    if (tempOrderView) {
      navigation('/temp-order');
      return;
    }
    navigation(-1);
  };

  const handleRegeneratePaymentLink = async () => {
    if (!order?.id || regeneratingLink) return;
    setRegeneratingLink(true);
    try {
      const result: any = await RegenerateOrderPaymentLink.create({
        orderId: order.id,
      });
      if (result?.paymentUrl) {
        setPaymentLinkDialog({ open: true, url: result.paymentUrl });
        toast.success(
          result.whatsappSent
            ? 'Payment link regenerated and sent via WhatsApp'
            : 'Payment link regenerated (WhatsApp not sent — check customer phone)'
        );
      } else {
        toast.warn('Payment link regenerated but no URL was returned');
      }
    } catch (err: any) {
      toast.error(
        err?.message ||
          err?.data?.message ||
          'Failed to regenerate payment link'
      );
    } finally {
      setRegeneratingLink(false);
    }
  };

  const handleCopyPaymentLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentLinkDialog.url);
      toast.success('Payment link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const base64ToBlob = (base64: any, mime: string) => {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mime });
  };

  const handleDownloadInvoice = async () => {
    try {
      const decodeParam = decryptString(params?.id ?? '');
      const result = await dispatch(
        getDownloadInvoice({ id: decodeParam })
      ).unwrap();
      if (IsNotNullOrEmpty(result)) {
        const pdfBlob = base64ToBlob(result, 'application/pdf');
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url);
      }
    } catch (error) {
      console.error('Error downloading the file:', error);
    }
  };

  const handleDownloadOrderDetails = async () => {
    try {
      setIsGeneratingPDF(true);
      if (order) {
        // Use the existing order data instead of making a separate API call
        const orderDetails = {
          order: {
            id: order.id,
            orderNumber: order.orderNumber || '',
            orderDate: order.orderDate,
            status: order.status,
            totalAmount: order.totalAmount,
            promoCodeDiscountAmount: order.promoCodeDiscountAmount,
            walletCreditUsed: order.walletCreditUsed,
            cardMessage: order.cardMessage,
            shippingAddress: order.shippingAddress,
            deliveryDate: order.deliveryDate,
            additionalInfo: order.additionalInfo,
            recipientInfo: order.recipientInfo,
            userDetails: order.userDetails,
            country_payment_method: order.country_payment_method,
            promo_code_usage: order.promo_code_usage,
            specialRequest: order.specialRequest,
            order_bundle_items: order.order_bundle_items || [],
          },
          orderItems: (() => {
            let cakeIndex = 0;
            let addonIndex = 0;
            
            return order.order_items?.map((item: any) => {
              if (item.order_cake) {
                cakeIndex++;
                return {
                  type: 'cake',
                  index: cakeIndex,
                  qty: item.qty,
                  unitPrice: item.unitPrice,
                  cake: {
                    id: item.order_cake.id,
                    shapeLabelEn: item.order_cake.shapeLabelEn,
                    shapeLabelAr: item.order_cake.shapeLabelAr,
                    colorCode: item.order_cake.colorCode,
                    writeOnCake: item.order_cake.writeOnCake,
                    printImageUrl: item.order_cake.printImageUrl,
                    instruction: item.order_cake.instruction,
                    shape: item.order_cake.shape,
                    flavor: item.order_cake.flavor,
                    category_cake: item.order_cake.category_cake,
                    topping_category_item: item.order_cake.topping_category_item,
                    displayMode: item.order_cake.displayMode,
                    cakeBoxId: item.order_cake.cakeBoxId || null,
                    cakeBox:
                      item.order_cake.cakeBox ||
                      (item.order_cake as any).cake_box ||
                      null,
                  }
                };
              } else if (item.addon_product) {
                addonIndex++;
                return {
                  type: 'addon',
                  index: addonIndex,
                  qty: item.qty,
                  unitPrice: item.unitPrice,
                  addon: item.addon_product
                };
              }
              return null;
            }).filter(Boolean);
          })()
        };
        
        // Generate PDF in frontend using the order details and pre-generated merged images
        await generateOrderDetailsPDF(orderDetails, assetUrl, mergedImages);
      }
    } catch (error) {
      console.error('Error downloading the file:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const generateOrderDetailsPDF = async (orderDetails: any, assetUrl: string, mergedImages: string[] = []) => {
    try {
      console.log("Generating PDF using HTML-to-PDF approach...");
      console.log("Using pre-existing merged images:", mergedImages.length, "images available");
      
      const preparedDetails = await prepareOrderBundleImagesForPDF(
        orderDetails,
        assetUrl
      );
      const htmlContent = generateOrderDetailsHTML(
        preparedDetails,
        assetUrl,
        mergedImages,
        paletteColors
      );
      
      // Generate PDF from HTML string
      await generatePDFFromHTMLString(htmlContent, {
        filename: `Order_Details_${orderDetails.order.orderNumber}.pdf`,
        format: 'a4',
        orientation: 'portrait',
        quality: 0.98,
        scale: 2
      });
      
      console.log('PDF generated successfully using HTML-to-PDF approach');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <Page title={tempOrderView ? 'View Temporary Order' : 'View Order'}>
      {/* Loading Overlay for PDF Generation */}
      {isGeneratingPDF && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <Loader />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Generating PDF...
          </Typography>
        </div>
      )}
      
      <Container maxWidth='xl'>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <Grid
              container
              sx={{ mb: 5 }}
              spacing={2}
            >
              <Grid
                item
                md={6}
                sm={12}
              >
                <Typography
                  variant='h4'
                  gutterBottom
                >
                  {tempOrderView ? 'Temporary Order Details' : 'Order Details'}
                </Typography>
                <Breadcrumbs
                  links={[
                    { name: 'Dashboard', href: '/' },
                    ...(tempOrderView
                      ? [{ name: 'Temporary Orders', href: '/temp-order' }]
                      : []),
                  ]}
                />
              </Grid>

              <Grid
                item
                md={6}
                sm={12}
                container
                alignItems='center'
                justifyContent='flex-end'
                gap={2}
              >
                <Button
                  onClick={handleDownloadInvoice}
                  variant='contained'
                  startIcon={<FaDownload />}
                >
                  {'Download Invoice'}
                </Button>
                <Button
                  onClick={handleDownloadOrderDetails}
                  variant='outlined'
                  startIcon={isGeneratingPDF ? <Loader /> : <FaDownload />}
                  disabled={isGeneratingPDF || !imagesLoaded}
                >
                  {isGeneratingPDF ? 'Generating PDF...' : 
                   !imagesLoaded ? 'Loading Images...' : 'Download Order Details'}
                </Button>
                {order?.status !== OrderStatus.cancelled &&
                  (userRole === 'admin' || userRole === 'orderManager') && (
                    <Button
                      variant='contained'
                      color='error'
                      endIcon={<Iconify icon={'eva:close-circle-outline'} />}
                      onClick={onOpen}
                    >
                      {'Cancel'}
                    </Button>
                  )}

                {!tempOrderView &&
                  (userRole === 'admin' || userRole === 'orderManager') && (
                  <CheckLateOrdersButton />
                )}

                {tempOrderView &&
                  (userRole === 'admin' || userRole === 'orderManager') && (
                    <Button
                      variant='contained'
                      startIcon={
                        regeneratingLink ? (
                          <CircularProgress size={18} color='inherit' />
                        ) : (
                          <WhatsAppIcon />
                        )
                      }
                      onClick={handleRegeneratePaymentLink}
                      disabled={regeneratingLink}
                      sx={{
                        backgroundColor: '#25D366 !important',
                        '&:hover': { backgroundColor: '#1da851 !important' },
                      }}
                    >
                      {'Regenerate payment link'}
                    </Button>
                  )}

                <Button
                  onClick={handleBack}
                  variant='contained'
                  startIcon={<ArrowBackIcon />}
                >
                  {'Back'}
                </Button>
              </Grid>
            </Grid>

            {!tempOrderView && order && order.status !== 'pending' && (
              <Box sx={{ mb: 3 }}>
                <ExactDeliverySlotSelector 
                  orderId={order.id} 
                  deliveryDate={order.deliveryDate?.date}
                  allowEdit={order.status === 'confirmed'}
                />
              </Box>
            )}

            <ViewOrder
              order={order}
              setOrder={setOrder}
              tempOrderView={tempOrderView}
            />

            <Dialog
              fullWidth
              open={toggle}
              onClose={handleClose}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'
            >
              <DialogTitle id='alert-dialog-title'>Order Cancel</DialogTitle>
              <IconButton
                aria-label='close'
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
              <DialogContent dividers>
                <Typography
                  variant='h6'
                  gutterBottom
                >
                  Are you sure you want to cancel the order?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      color='primary'
                      name='activeState'
                      onChange={(e) => setRefundForCancel(e.target.checked)}
                      checked={refundForCancel}
                    />
                  }
                  label={'Refund'}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleYes}
                  variant='contained'
                >
                  Yes
                </Button>
                <Button
                  onClick={handleNo}
                  variant='outlined'
                >
                  No
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={paymentLinkDialog.open}
              onClose={() => setPaymentLinkDialog({ open: false, url: '' })}
              maxWidth='sm'
              fullWidth
            >
              <DialogTitle>Payment link regenerated</DialogTitle>
              <DialogContent>
                <Typography variant='body2' sx={{ mb: 2 }}>
                  A new payment link was created and WhatsApp was sent to the
                  customer (when a phone number is available). You can also copy
                  the link below.
                </Typography>
                <TextField
                  fullWidth
                  size='small'
                  value={paymentLinkDialog.url}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='Copy payment link'
                          onClick={handleCopyPaymentLink}
                          edge='end'
                        >
                          <ContentCopyIcon fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCopyPaymentLink}>Copy link</Button>
                <Button
                  variant='contained'
                  onClick={() => setPaymentLinkDialog({ open: false, url: '' })}
                  sx={{
                    backgroundColor: '#EE3750 !important',
                    '&:hover': { backgroundColor: '#EE3750 !important' },
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Container>
    </Page>
  );
};

export default ViewManagePO;
